// The one accessible tab-strip implementation for every in-place content
// switcher in the app (WAI-ARIA APG "Tabs" pattern: a `role="tablist"`
// container, `role="tab"` children with `aria-selected` and a roving
// tabindex, automatic activation on arrow/Home/End). Before this, the
// PresetSwitcher/BottomDock (`.ed-tabstrip`), the pre-save diff modal
// (`.editor-diff-tabs`), the Wiki page editor's view-mode switcher, the
// Character Studio's character/pose/clip selectors, and the Legal/Showcase
// filter chips (`.range-chip`) each reimplemented tab-switching
// independently, all as plain `<button>`s with no tab semantics at all.
//
// Lives in the engine layer, not src/studio/: two consumers
// (PreSaveDiffModal, CharacterStudio) are engine-internal, and the engine
// boundary (scripts/check-engine-boundary.mjs) only allows imports from
// within src/engine itself. src/studio/ already imports engine internals
// directly via relative paths for exactly this "shared internal-only piece,
// not part of the published @sstk/build-kit surface" case (see e.g.
// src/studio/gunsmith/store/gunsmithStore.ts importing
// '../../../engine/editor/persistence/ProjectPersistence') - this follows
// the same convention rather than adding a new public export.
//
// Deliberately does not attempt to unify page-navigation switchers (the
// project shell's rail, Docs' sidebar) - those are real route changes, not
// in-place content swaps, and belong to `role="navigation"` +
// `aria-current`, a different ARIA pattern. See src/studio/components/
// SectionNav.tsx for that one. Account settings' own section switcher looks
// like a sidebar but is actually state-driven, not route-driven (no URL
// change) - that's a tablist wearing a sidebar's clothes, so it uses this
// component's 'rail' variant rather than SectionNav.
import { useRef, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';

export interface TabDescriptor {
  id: string;
  /** Full tab content, e.g. `<>Issues <span>{count}</span></>` for a
   * labelled count badge - composed by the caller rather than a dedicated
   * prop, since only one current consumer (PreSaveDiffModal) needs it. */
  label: ReactNode;
  /** Renders the same danger-colored accent PreSaveDiffModal's Issues/Linter
   * tabs use when they have something to flag. */
  hasError?: boolean;
  /** Native tooltip, e.g. Character Studio's animation-clip descriptions. */
  title?: string;
}

export interface TabStripProps {
  tabs: readonly TabDescriptor[];
  activeId: string;
  onSelect: (id: string) => void;
  /** 'strip' (default) matches the editor's existing `.ed-tabstrip` look,
   * itself deliberately kept identical to dockview's own rendered tabs
   * (see editor-theme.css's `.dockview-theme-abyss .dv-tab` comment) - so a
   * BottomDock or diff-modal tab strip reads as the same control as a
   * dockview panel's own tabs. 'compact' matches the existing `.range-chip`
   * pill look used for filters. 'rail' matches the project shell's vertical
   * `.pj-rail-link` look, for an in-place content switch that happens to be
   * laid out as a sidebar (e.g. Account settings' section switcher) - as
   * opposed to real page-to-page navigation, which is a `<nav>` +
   * `aria-current` job, not a tablist; see SectionNav.tsx for that case.
   * 'segmented' matches the flush, bordered, equal-width button-group look
   * used for a handful of mode switchers (Pixel Studio's Sprite/Tilemap/
   * Place mode, Game Data's Items/Quests/Dialogue) - visually distinct from
   * all three other variants, not a stand-in for any of them. */
  variant?: 'strip' | 'compact' | 'rail' | 'segmented';
  /** A required, human-readable label for the tablist itself (e.g. "Editor
   * bottom dock", "Save conflict sections") - screen readers announce it
   * when entering the tablist. Not shown visually. */
  listLabel: string;
  /** Rendered after the last tab, inside the same row (e.g. Workspace's
   * "Reset layout" button). */
  after?: ReactNode;
  /** Set only when the caller actually renders a distinct panel element per
   * tab (with a matching `id={panelId(tab.id)}`, e.g. via `TabPanel` or a
   * manual wrapper) - this is what makes `aria-controls` true rather than a
   * dangling reference. Most consumers here are filters or selectors that
   * affect broader page/document state rather than swapping one discrete
   * panel (Character Studio's character/pose/clip pickers, the asset-library
   * category filters, Showcase's game filter) - `aria-controls` pointing at
   * nothing would be worse than omitting it, so it defaults to off. */
  controlsPanels?: boolean;
  /** Namespaces this instance's `tab-*`/`tabpanel-*` DOM ids against a
   * caller-chosen string. Only needed when more than one `TabStrip` can be
   * mounted on the same page with tab `id`s drawn from an open-ended/
   * user-authored domain that could collide across instances (e.g. Character
   * Studio mounts six strips at once - character/pose/clip ids are creator-
   * named, so a pose and a clip could in principle share an id). Strips whose
   * ids come from a small fixed, code-defined set (route/mode names, etc.)
   * don't need this - collision is already structurally impossible. */
  scope?: string;
  className?: string;
  style?: CSSProperties;
}

/** id/href-stable so a caller can build `aria-controls`/`aria-labelledby`
 * pairs against a panel it renders itself; see `tabId`/`panelId` below. Pass
 * the same `scope` given to the `TabStrip` (or omit it on both, the default)
 * so ids agree - see `TabStripProps.scope`'s own doc for when this matters. */
export function tabId(id: string, scope?: string): string {
  return scope ? `tab-${scope}-${id}` : `tab-${id}`;
}
export function panelId(id: string, scope?: string): string {
  return scope ? `tabpanel-${scope}-${id}` : `tabpanel-${id}`;
}

const CONTAINER_CLASS: Record<NonNullable<TabStripProps['variant']>, string> = {
  strip: 'ed-tabstrip',
  compact: 'ss-tabstrip--compact',
  rail: 'pj-rail',
  segmented: 'ed-segmented',
};
const TAB_CLASS: Record<NonNullable<TabStripProps['variant']>, string> = {
  strip: 'ed-tabstrip-tab',
  compact: 'range-chip',
  rail: 'pj-rail-link',
  segmented: 'ed-segmented-tab',
};

export function TabStrip({ tabs, activeId, onSelect, variant = 'strip', listLabel, after, controlsPanels, scope, className, style }: TabStripProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const vertical = variant === 'rail';
  // If activeId matches nothing (a caller's own state has a value outside
  // this strip's own tabs, e.g. Wiki's separate 'history' mode), fall back
  // to making the first tab reachable by Tab key rather than leaving every
  // tab at tabIndex -1 and the whole strip unfocusable.
  const noneActive = tabs.length > 0 && !tabs.some((t) => t.id === activeId);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (tabs.length === 0) return;
    // Mirror the tabIndex fallback above: with no matching activeId, the
    // first tab is the one actually focused (it's the only one in the Tab
    // order), so arrow-key math has to be relative to it rather than
    // bailing out just because activeId itself doesn't resolve to an index.
    const rawIdx = tabs.findIndex((t) => t.id === activeId);
    const idx = rawIdx === -1 ? 0 : rawIdx;
    const forwardKey = vertical ? 'ArrowDown' : 'ArrowRight';
    const backKey = vertical ? 'ArrowUp' : 'ArrowLeft';
    let next = -1;
    if (e.key === forwardKey) next = (idx + 1) % tabs.length;
    else if (e.key === backKey) next = (idx - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = tabs.length - 1;
    if (next === -1) return;
    e.preventDefault();
    const nextTab = tabs[next];
    if (!nextTab) return;
    onSelect(nextTab.id);
    listRef.current?.querySelector<HTMLElement>(`[data-tab-id="${CSS.escape(nextTab.id)}"]`)?.focus();
  };

  return (
    // Visual row lives on this outer element (className/style land here, not
    // on the tablist below) - `after` (e.g. BottomDock's collapse button) is
    // real UI in the same row but isn't a tab, so per the WAI-ARIA tablist
    // containment model it can't be a child of role="tablist" itself. The
    // inner tablist uses `display: contents` so it contributes no box of its
    // own: its <button role="tab"> children become direct flex items of this
    // outer row (preserving the exact previous layout, gaps included) while
    // `after` sits beside it as a DOM sibling, outside the tablist.
    <div className={CONTAINER_CLASS[variant] + (className ? ` ${className}` : '')} style={style}>
      <div
        ref={listRef}
        role="tablist"
        aria-label={listLabel}
        aria-orientation={vertical ? 'vertical' : undefined}
        onKeyDown={onKeyDown}
        style={{ display: 'contents' }}
      >
        {tabs.map((tab, i) => {
          const active = tab.id === activeId;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              id={tabId(tab.id, scope)}
              aria-selected={active}
              aria-controls={controlsPanels ? panelId(tab.id, scope) : undefined}
              tabIndex={active || (noneActive && i === 0) ? 0 : -1}
              data-tab-id={tab.id}
              title={tab.title}
              onClick={() => onSelect(tab.id)}
              className={TAB_CLASS[variant] + (active ? ' is-active' : '') + (tab.hasError ? ' has-error' : '')}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      {after}
    </div>
  );
}

/** Optional convenience wrapper for the switched content - purely
 * `role="tabpanel"` plumbing (`hidden` rather than unmounting, so a
 * consumer that needs to preserve content state across a tab switch, e.g.
 * an in-progress scroll position or focus, still can). Not required: a
 * caller already conditionally rendering its own panel can just pass
 * `id={tabId(...)}`/`aria-labelledby` directly instead. */
export function TabPanel({ id, activeId, scope, children }: { id: string; activeId: string; scope?: string; children: ReactNode }) {
  return (
    <div role="tabpanel" id={panelId(id, scope)} aria-labelledby={tabId(id, scope)} hidden={id !== activeId} style={{ minHeight: 0, minWidth: 0 }}>
      {children}
    </div>
  );
}
